import "./App.css";

import { Outlet, useNavigate } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupIcon from "@mui/icons-material/Group";
import PaidIcon from "@mui/icons-material/Paid";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import { AppProvider } from "@toolpad/core/react-router-dom";
import SourceIcon from "@mui/icons-material/Source";
import { Navigation, type Session } from "@toolpad/core/AppProvider";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import {
  useTheme,
  IconButton,
  Badge,
  Tooltip,
  Box,
  Popover,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./context/auth";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MailIcon from "@mui/icons-material/Mail";
import { useFilteredNavigation } from "./utilities/permissions";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUnreadInboundSmsCount,
  getUnreadInboundSms,
  markSmsAsRead,
} from "./api/sms-history";
import UnreadSmsList from "./components/UnreadSmsList";

const NAVIGATION: Navigation = [
  {
    kind: "header",
    title: "اصلی",
  },
  {
    title: "کار های من",
    icon: <AssignmentIndIcon />,
  },
  {
    segment: "works",
    title: "کار ها",
    icon: <AssignmentIcon />,
  },
  {
    segment: "users/customer",
    title: "مشتریان",
    icon: <GroupIcon />,
  },
  {
    title: "کارمندان",
    segment: "users/editor",
    icon: <SupportAgentIcon />,
  },
  {
    kind: "divider",
  },
  {
    kind: "header",
    title: "مدیریت  مشتریان",
  },
  {
    segment: "tasks",
    title: "وظایف",
    icon: <FormatListBulletedIcon />,
  },

  {
    segment: "followups-schedule",
    title: "پیگیری های دوره ای",
    icon: <ContentPasteSearchIcon />,
  },

  {
    segment: "agreements",
    title: "قرارداد ها",
    icon: <CurrencyExchangeIcon />,
    children: [
      {
        title: "قرارداد ها",
      },
      {
        segment: "services",
        title: "خدمات",
      },
      {
        segment: "base",
        title: "پایه قرارداد",
      },
      {
        segment: "followup-groups",
        title: "گروه پیگیری",
      },
    ],
  },
  {
    segment: "transactions",
    title: "آخرین تراکنش ها",
    icon: <PaidIcon />,
  },
  // {
  //   segment: "sales-funnel",
  //   title: "قیف فروش",
  //   icon: <FilterAltIcon />,
  // },
  {
    segment: "base",
    title: "تعاریف پایه",
    icon: <SourceIcon />,
  },
  {
    segment: "processes",
    title: "فرایندها",
    icon: <AccountTreeIcon />,
  },

  {
    segment: "setting",
    title: "تنظیمات",
    icon: <SettingsIcon />,
    children: [
      {
        segment: "sales",
        title: "صف پیامک های خودکار",
      },
      {
        segment: "sms-history",
        title: "تاریخچه پیام ها",
      },
      {
        segment: "systems",
        title: "سامانه ها",
      },
      {
        segment: "process-sections",
        title: "بخش های فرایند",
      },
      // {
      //   segment: "traffic",
      //   title: "فایل ها",
      // },
      // {
      //   segment: "traffic",
      //   title: "تنظیمات",
      // },
      // {
      //   segment: "traffic",
      //   title: "پروفایل من",
      // },
    ],
  },
];

function App() {
  const theme = useTheme();
  function stringToColor(string: string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  }
  function stringAvatar(name: string) {
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
    };
  }

  const [session, setSession] = useState<any>();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // فیلتر کردن منوها بر اساس دسترسی‌های کاربر
  const filteredNavigation = useFilteredNavigation(NAVIGATION);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();

  // دریافت تعداد پیام‌های خوانده نشده
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["unreadSmsCount", user?.token],
    queryFn: () => getUnreadInboundSmsCount(user?.token || ""),
    enabled: !!user?.token,
    refetchInterval: 30000, // هر 30 ثانیه یکبار refresh می‌کند
  });

  // دریافت لیست پیام‌های خوانده نشده
  const {
    data: unreadSms = [],
    isLoading: isLoadingUnreadSms,
    refetch: refetchUnreadSms,
  } = useQuery({
    queryKey: ["unreadSms", user?.token],
    queryFn: () => getUnreadInboundSms(user?.token || ""),
    enabled: !!user?.token && !!anchorEl, // فقط وقتی Popover باز است
  });

  // Mutation برای mark as read
  const markAsReadMutation = useMutation({
    mutationFn: (smsId: number) => markSmsAsRead(user?.token || "", smsId),
    onSuccess: () => {
      // Refresh کردن لیست پیام‌های خوانده نشده و تعداد
      queryClient.invalidateQueries({ queryKey: ["unreadSmsCount"] });
      queryClient.invalidateQueries({ queryKey: ["unreadSms"] });
      refetchUnreadSms();
    },
  });

  const handleMailboxClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (smsId: number) => {
    markAsReadMutation.mutate(smsId);
  };

  const open = Boolean(anchorEl);
  const id = open ? "mailbox-popover" : undefined;

  useEffect(() => {
    if (!localStorage.getItem("Token")) {
      navigate("/sign-in");
    }
    if (user) {
      setSession({
        user: {
          name: user?.userName,
          email: user?.email,
          token: user?.token,
          roleName: user?.roleName,
          permissions: user?.permissions,
          //image: <Avatar {...stringAvatar("teifco portal")} />,
        },
      });
    }
  }, [user]);

  const authentication = useMemo(() => {
    return {
      signIn: () => {},
      signOut: () => {
        setSession(null);
        signOut();
        navigate("/sign-in");
      },
    };
  }, []);

  return (
    <>
      <AppProvider
        navigation={filteredNavigation}
        authentication={authentication}
        session={session}
        theme={theme}
        branding={{
          logo: <img src="https://mui.com/static/logo.png" alt="تکنو حساب" />,
          title: " تکنوحساب",
        }}
      >
        <Outlet />
      </AppProvider>
      {/* آیکون میل باکس با موقعیت ثابت */}
      {user?.token && (
        <Box
          sx={{
            position: "fixed",
            top: 12,
            right: { xs: 200, sm: 250, md: 280 }, // فاصله بیشتر از دکمه "وظیفه جدید"
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            pointerEvents: "auto",
          }}
        >
          <Tooltip title="پیام‌های دریافتی">
            <IconButton
              onClick={handleMailboxClick}
              sx={{
                color: theme.palette.mode === "dark" ? "#fff" : "#1976d2",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(25, 118, 210, 0.15)",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.25)"
                      : "rgba(25, 118, 210, 0.25)",
                },
                boxShadow: 3,
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(25, 118, 210, 0.2)"}`,
              }}
              size="medium"
            >
              <Badge
                badgeContent={unreadCount > 0 ? unreadCount : undefined}
                color="error"
                max={99}
              >
                <MailIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              mt: 1,
            }}
          >
            <UnreadSmsList
              unreadSms={unreadSms}
              unreadCount={unreadCount}
              isLoading={isLoadingUnreadSms}
              onMarkAsRead={handleMarkAsRead}
              isMarkingAsRead={markAsReadMutation.isPending}
              onClose={handleClose}
            />
          </Popover>
        </Box>
      )}
    </>
  );
}

export default App;
